import React from 'react';
import { useTranslation } from 'react-i18next';
import { CreditCard, Calendar, AlertTriangle, RefreshCw, Settings } from 'lucide-react';
import { Card, CardHeader, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Subscription, Plan } from '../../model/professional/subscription-model';
import { format } from 'date-fns';
import { fr, enUS } from 'date-fns/locale';

interface CurrentPlanCardProps {
  subscription: Subscription;
  plan: Plan | null;
  onCancel: () => void;
  onReactivate: () => void;
  onChangeSubscription: () => void;
  isLoading?: boolean;
}

const CurrentPlanCard: React.FC<CurrentPlanCardProps> = ({
  subscription,
  plan,
  onCancel,
  onReactivate,
  onChangeSubscription,
  isLoading = false
}) => {
  const { t, i18n } = useTranslation();

  const getStatusBadge = () => {
    // Si annulé mais encore actif jusqu'à la fin de période
    if (subscription.cancelAtPeriodEnd && (subscription.status === 'active' || subscription.status === 'trialing')) {
      // Utiliser trialEnd si en période d'essai, sinon currentPeriodEnd
      const endDate = subscription.status === 'trialing' && subscription.trialEnd
        ? subscription.trialEnd
        : subscription.currentPeriodEnd;

      return (
        <div className="text-right">
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400">
            {subscription.status === 'trialing' ? t('subscription.status.trialing') : t('subscription.status.active')}
          </span>
          <p className="text-xs text-orange-600 dark:text-orange-400 mt-1">
            {t('subscription.validUntil', { date: formatDate(endDate) })}
          </p>
        </div>
      );
    }

    switch (subscription.status) {
      case 'active':
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
            {t('subscription.status.active')}
          </span>
        );
      case 'trialing':
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400">
            {t('subscription.status.trialing')}
          </span>
        );
      case 'past_due':
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400">
            {t('subscription.status.pastDue')}
          </span>
        );
      case 'canceled':
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400">
            {t('subscription.status.canceled')}
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300">
            {subscription.status}
          </span>
        );
    }
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      const locale = i18n.language === 'fr' ? fr : enUS;
      return format(date, 'PPP', { locale });
    } catch {
      return dateString;
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              {t('subscription.currentPlanTitle')}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {t('subscription.managePlan')}
            </p>
          </div>
          {getStatusBadge()}
        </div>
      </CardHeader>

      <CardContent>
        {/* Informations du plan */}
        <div className="space-y-4">
          {/* Plan et prix */}
          <div className="flex items-center justify-between p-4 bg-gradient-to-br from-primary/5 to-purple-500/5 dark:from-primary-dark/10 dark:to-purple-900/10 rounded-xl">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-primary/20 dark:bg-primary-dark/30 flex items-center justify-center">
                <CreditCard className="w-6 h-6 text-primary dark:text-primary-light" />
              </div>
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {t('subscription.yourPlan')}
                </p>
                <p className="text-lg font-semibold text-gray-900 dark:text-white">
                  {plan?.name || subscription.planName}
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {Number(subscription.planPrice).toFixed(2)}€
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {t('subscription.perMonth')}
              </p>
            </div>
          </div>

          {/* Période en cours */}
          <div className="flex items-start gap-3 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
            <Calendar className="w-5 h-5 text-gray-400 dark:text-gray-500 mt-0.5" />
            <div className="flex-1">
              {subscription.status === 'trialing' && subscription.trialEnd ? (
                <>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {t('subscription.trialEnds')}
                  </p>
                  <p className="text-sm text-blue-600 dark:text-blue-400 mt-1">
                    {formatDate(subscription.trialEnd)}
                  </p>
                  <p className="text-sm font-medium text-gray-900 dark:text-white mt-3">
                    {t('subscription.billingPeriod')}
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    {formatDate(subscription.currentPeriodStart)} - {formatDate(subscription.currentPeriodEnd)}
                  </p>
                </>
              ) : (
                <>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {t('subscription.billingPeriod')}
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    {formatDate(subscription.currentPeriodStart)} - {formatDate(subscription.currentPeriodEnd)}
                  </p>
                </>
              )}
            </div>
          </div>

          {/* Avertissement d'annulation */}
          {subscription.cancelAtPeriodEnd && (
            <div className="flex items-start gap-3 p-4 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-xl">
              <AlertTriangle className="w-5 h-5 text-orange-600 dark:text-orange-400 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-orange-900 dark:text-orange-300">
                  {t('subscription.cancelWarning')}
                </p>
                <p className="text-sm text-orange-700 dark:text-orange-400 mt-1">
                  {t('subscription.cancelDescription', {
                    date: formatDate(
                      subscription.status === 'trialing' && subscription.trialEnd
                        ? subscription.trialEnd
                        : subscription.currentPeriodEnd
                    )
                  })}
                </p>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <Button
              onClick={onChangeSubscription}
              disabled={isLoading}
              variant="outline"
              className="flex-1"
            >
              <Settings className="w-4 h-4 mr-2" />
              {t('subscription.changeSubscription')}
            </Button>

            {subscription.cancelAtPeriodEnd ? (
              <Button
                onClick={onReactivate}
                disabled={isLoading}
                className="flex-1"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                {t('subscription.reactivate')}
              </Button>
            ) : (
              <Button
                onClick={onCancel}
                disabled={isLoading}
                variant="outline"
                className="flex-1 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 border-red-200 dark:border-red-800 hover:bg-red-50 dark:hover:bg-red-900/20"
              >
                {t('subscription.cancelPlan')}
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default CurrentPlanCard;
